import { useQueryClient } from "@tanstack/react-query";
import { useWeather, weatherKeys } from "@/hooks/useWeather";
import type { WeatherForecastDay } from "@/lib/api/types";

function tempColor(t: number, kind: "high" | "low"): string {
  if (kind === "high" && t > 85) return "#A05A38";
  if (kind === "low" && t < 40) return "#3B6FAE";
  return "#1A1620";
}

function ForecastPill({ day }: { day: WeatherForecastDay }) {
  return (
    <div
      className="flex w-14 shrink-0 flex-col items-center gap-1 rounded-2xl py-2"
      style={{ background: "#FFFCF8", border: "1px solid #EAE4DA" }}
      data-testid="forecast-pill"
    >
      <span className="text-xs font-medium" style={{ color: "#9A8A78" }}>
        {day.dayLabel}
      </span>
      <span
        className="text-sm font-semibold"
        style={{ color: tempColor(day.highF, "high") }}
      >
        {Math.round(day.highF)}°
      </span>
      <span
        className="text-xs"
        style={{ color: tempColor(day.lowF, "low") }}
      >
        {Math.round(day.lowF)}°
      </span>
    </div>
  );
}

export function WeatherWidget() {
  const { data, isLoading, isError } = useWeather();
  const queryClient = useQueryClient();

  if (isLoading) {
    return (
      <section
        data-testid="weather-widget"
        data-state="loading"
        className="animate-pulse rounded-2xl p-4"
        style={{
          background: "#FFFFFF",
          border: "1px solid #EAE4DA",
          minHeight: "180px",
        }}
      >
        <div
          className="h-12 w-24 rounded"
          style={{ background: "#EAE4DA" }}
        />
        <div
          className="mt-4 h-16 rounded"
          style={{ background: "#EAE4DA" }}
        />
        <div
          className="mt-4 h-10 rounded"
          style={{ background: "#EAE4DA" }}
        />
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section
        data-testid="weather-widget"
        data-state="error"
        className="rounded-2xl p-4"
        style={{ background: "#FFFFFF", border: "1px solid #EAE4DA" }}
      >
        <p className="text-sm" style={{ color: "#1A1620" }}>
          Couldn&apos;t load weather.
        </p>
        <button
          type="button"
          onClick={() =>
            void queryClient.invalidateQueries({ queryKey: weatherKeys.all })
          }
          className="mt-2 text-sm underline"
          style={{ color: "#8A4E9C" }}
        >
          Retry
        </button>
      </section>
    );
  }

  const { current, forecast, contextualAdvice } = data;
  const adviceBorder =
    contextualAdvice.severity === "warning" ? "#A05A38" : "#EAE4DA";

  return (
    <section
      data-testid="weather-widget"
      data-state="loaded"
      className="space-y-3 rounded-2xl p-4"
      style={{ background: "#FFFFFF", border: "1px solid #EAE4DA" }}
    >
      <div className="flex items-end justify-between">
        <div
          className="text-5xl font-semibold leading-none"
          style={{ color: "#1A1620" }}
          data-testid="weather-temp"
        >
          {Math.round(current.tempF)}°
        </div>
        <div className="text-right text-sm" style={{ color: "#9A8A78" }}>
          <p style={{ color: "#1A1620" }}>{current.condition}</p>
          <p className="mt-1">
            {current.windSpeed} {current.windDirection}
          </p>
        </div>
      </div>

      <div
        data-testid="weather-forecast"
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
      >
        {forecast.map((d) => (
          <ForecastPill key={d.date} day={d} />
        ))}
      </div>

      <div
        data-testid="weather-advice"
        className="rounded-xl px-3 py-2 text-sm"
        style={{
          background: "#FFFCF8",
          borderLeft: `3px solid ${adviceBorder}`,
          color: "#1A1620",
        }}
      >
        {contextualAdvice.message}
      </div>
    </section>
  );
}
